/**
 * 素材パネルからのURLキャプチャの入口 (docs/features/swipe-motion-clip-plan.md §11.2.8)。
 * 入口は1つ: 統合ダイアログ (スクロール動画モーダルの URL 導入ステップ) を開き、フッターで
 * 「スクリーンショット画像を生成」/「スクロール動画を生成」を選ばせる。
 * 生成キューの無い画面では available=false (入口を出さない)。
 */
import { lazy, Suspense, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useGenerationJobsSafe } from '../../components/generation/state/jobsContext';
import { useToast } from '../../hooks/useToast';
import type { SwipeClipParams } from '../swipeClip/schema';
import { startStandaloneSwipeClipJob } from '../swipeClip/standaloneJob';
import { captureHostOf } from './api';
import { useUrlCaptureStarter } from './useUrlCaptureStarter';

const SwipeClipModal = lazy(() => import('../swipeClip/SwipeClipModal'));

export interface UrlCaptureEntry {
  available: boolean;
  open: () => void;
  /** 入口が開くダイアログ。呼び出し側のツリーのどこかに置く */
  modals: ReactNode;
}

export function useUrlCaptureEntry(): UrlCaptureEntry {
  const starter = useUrlCaptureStarter();
  const jobs = useGenerationJobsSafe();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  /** スクロール動画のキュー表示名に使う、撮ったページのホスト */
  const hostRef = useRef<string | null>(null);

  const trackedStarter = useMemo(() => {
    if (!starter) return null;
    return {
      start: async (body: Parameters<typeof starter.start>[0]) => {
        hostRef.current = captureHostOf(body.url);
        return starter.start(body);
      },
    };
  }, [starter]);

  const handleSubmitScrollVideo = async (params: SwipeClipParams) => {
    if (!jobs) throw new Error('この画面ではスクロール動画を作成できません');
    await startStandaloneSwipeClipJob(
      { setActiveJobs: jobs.setActiveJobs, setShowJobQueueModal: jobs.setShowJobQueueModal, toast, queryClient },
      { params, label: hostRef.current ?? 'キャプチャ' },
    );
  };

  const modals = isOpen && trackedStarter ? (
    <Suspense fallback={null}>
      <SwipeClipModal urlCapture={trackedStarter} onClose={() => setIsOpen(false)} onSubmit={handleSubmitScrollVideo} />
    </Suspense>
  ) : null;

  return {
    available: !!trackedStarter,
    open: () => setIsOpen(true),
    modals,
  };
}
